// src/components/SettingsPage.js
import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import { motion } from 'framer-motion';
import PageHeader from './common/PageHeader';
import {
    Container, Typography, Box, CircularProgress, Alert, Paper, TextField, Button, Snackbar, Tabs, Tab,
    Grid, Switch, FormControlLabel, InputAdornment, IconButton, Stack, Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const pageVariants = {
    initial: { opacity: 0, x: "-5vw" },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: "5vw" }
};
const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.5
};

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function SettingsPage() {
    const [value, setValue] = useState(0); // Tab index
    const [formData, setFormData] = useState({
        // SMTP
        email_host: '',
        email_port: 587,
        email_host_user: '',
        email_host_password: '',
        email_use_tls: true,
        email_from_address: '',
        // KB
        ai_assistant_enabled: true,
        ai_prompt: '',
        knowledge_base_text: '',
        knowledge_base_file: null, // URL or null
    });

    // For file upload
    const [selectedFile, setSelectedFile] = useState(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [testingEmail, setTestingEmail] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('تم حفظ الإعدادات بنجاح!');

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axiosInstance.get('/chatbot/settings/');
                setFormData({
                    email_host: response.data.email_host || '',
                    email_port: response.data.email_port || 587,
                    email_host_user: response.data.email_host_user || '',
                    email_host_password: '',
                    email_use_tls: response.data.email_use_tls !== undefined ? response.data.email_use_tls : true,
                    email_from_address: response.data.email_from_address || '',
                    ai_assistant_enabled: response.data.ai_assistant_enabled !== undefined ? response.data.ai_assistant_enabled : true,
                    ai_prompt: response.data.ai_prompt || '',
                    knowledge_base_text: response.data.knowledge_base_text || '',
                    knowledge_base_file: response.data.knowledge_base_file || null,
                });
            } catch (err) {
                console.error('Failed to load settings', err);
                setError('فشل تحميل الإعدادات.');
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleTabChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleDeleteFile = async () => {
        if (!window.confirm("هل أنت متأكد من حذف ملف المعرفة؟")) return;
        setSaving(true);
        try {
            await axiosInstance.put('/chatbot/settings/', { knowledge_base_file: null });
            setFormData(prev => ({ ...prev, knowledge_base_file: null }));
            setSnackbarOpen(true);
        } catch (err) {
            console.error("Failed to delete file:", err);
            setError("فشل حذف الملف.");
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');

        try {
            let dataToSend;
            let config = {};

            if (selectedFile) {
                const fd = new FormData();
                Object.keys(formData).forEach(key => {
                    if (key === 'knowledge_base_file') return;
                    if (key === 'email_host_password' && !formData[key]) return;
                    fd.append(key, formData[key]);
                });
                fd.append('knowledge_base_file', selectedFile);
                dataToSend = fd;
                config = { headers: { 'Content-Type': 'multipart/form-data' } };
            } else {
                dataToSend = { ...formData };
                delete dataToSend.knowledge_base_file;
                if (!dataToSend.email_host_password) delete dataToSend.email_host_password;
            }

            const response = await axiosInstance.put('/chatbot/settings/', dataToSend, config);

            setFormData(prev => ({
                ...prev,
                knowledge_base_file: response.data.knowledge_base_file
            }));
            setSelectedFile(null);
            setSnackbarMessage('تم حفظ الإعدادات بنجاح!');
            setSnackbarOpen(true);

        } catch (err) {
            console.error('Save failed', err);
            setError('فشل حفظ الإعدادات.');
        } finally {
            setSaving(false);
        }
    };

    const getFileUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        return `http://localhost:8000${url}`;
    };

    const handleFileOpen = () => {
        window.open(getFileUrl(formData.knowledge_base_file), '_blank');
    };

    const handleTestEmail = async () => {
        setTestingEmail(true);
        setError('');
        try {
            const payload = formData.testEmail ? { email: formData.testEmail } : {};
            const response = await axiosInstance.post('/chatbot/test-email/', payload);
            if (response.data.success) {
                setSnackbarMessage(response.data.message);
                setSnackbarOpen(true);
            } else {
                setError(response.data.error || 'فشل إرسال البريد التجريبي');
            }
        } catch (err) {
            console.error('Test email failed', err);
            setError(err.response?.data?.error || 'فشل إرسال البريد التجريبي');
        } finally {
            setTestingEmail(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', height: '100vh', alignItems: 'center' }}><CircularProgress /></Box>;
    }

    return (
        <motion.div initial="initial" animate="in" exit="out" variants={pageVariants} transition={pageTransition}>
            <Container maxWidth="lg">
                <PageHeader
                    title="الإعدادات"
                    subtitle="ضبط إعدادات النظام والبريد الإلكتروني"
                    variant="gradient"
                />

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Paper sx={{ width: '100%', mt: 2, borderRadius: 4, overflow: 'hidden' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={value} onChange={handleTabChange} aria-label="settings tabs">
                            <Tab icon={<AutoAwesomeIcon />} label="قاعدة المعرفة والذكاء الاصطناعي" iconPosition="start" />
                            <Tab icon={<EmailIcon />} label="إعدادات البريد (SMTP)" iconPosition="start" />
                        </Tabs>
                    </Box>

                    {/* Tab 1: Knowledge Base */}
                    <TabPanel value={value} index={0}>
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <Stack spacing={4}>
                                {/* AI Prompt Section */}
                                <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <AutoAwesomeIcon color="primary" sx={{ mr: 1 }} />
                                            <Typography variant="h6" fontWeight="bold">
                                                حالة مساعد الذكاء الاصطناعي
                                            </Typography>
                                        </Box>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.ai_assistant_enabled}
                                                    onChange={handleInputChange}
                                                    name="ai_assistant_enabled"
                                                    color="primary"
                                                />
                                            }
                                            label={formData.ai_assistant_enabled ? "مفعّل" : "معطّل"}
                                        />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        عند تعطيل المساعد، ستستمر الرسائل في الظهور داخل الشات بشكل طبيعي، لكن سيتم إيقاف الرد الآلي فقط حتى يتولى الفريق البشري المتابعة.
                                    </Typography>

                                    <Divider sx={{ my: 2 }} />

                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <AutoAwesomeIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6" fontWeight="bold">
                                            تعليمات النظام (System Prompt)
                                        </Typography>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        label="توجيه الذكاء الاصطناعي"
                                        name="ai_prompt"
                                        value={formData.ai_prompt}
                                        onChange={handleInputChange}
                                        placeholder="أنت مساعد ذكي لخدمة عملاء..."
                                        helperText="التعليمات الأساسية التي يتبعها الشات بوت في كل محادثة."
                                        variant="outlined"
                                    />
                                </Paper>

                                {/* Knowledge Base Text Section */}
                                <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <DescriptionIcon color="secondary" sx={{ mr: 1 }} />
                                        <Typography variant="h6" fontWeight="bold">
                                            نص قاعدة المعرفة
                                        </Typography>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={8}
                                        label="المحتوى النصي"
                                        name="knowledge_base_text"
                                        value={formData.knowledge_base_text}
                                        onChange={handleInputChange}
                                        placeholder="اكتب المعلومات، الأسئلة الشائعة، والسياسات هنا..."
                                        helperText="سيتم استخدام هذا النص كمرجع للرد على استفسارات العملاء."
                                        variant="outlined"
                                    />
                                </Paper>

                                {/* File Upload Section */}
                                <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <UploadFileIcon color="action" sx={{ mr: 1 }} />
                                        <Typography variant="h6" fontWeight="bold">
                                            ملف المعرفة (اختياري)
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        p: 4,
                                        border: '2px dashed',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                        bgcolor: 'background.default',
                                        textAlign: 'center'
                                    }}>
                                        {formData.knowledge_base_file && !selectedFile ? (
                                            <Stack alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    bgcolor: 'action.hover',
                                                    width: 'fit-content',
                                                    maxWidth: '100%'
                                                }}>
                                                    <DescriptionIcon color="primary" />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            direction: 'ltr',
                                                            fontWeight: 'medium',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: { xs: '200px', sm: '300px' }
                                                        }}
                                                    >
                                                        {decodeURIComponent(formData.knowledge_base_file.split('/').pop())}
                                                    </Typography>
                                                </Box>

                                                <Stack direction="row" spacing={2}>
                                                    <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} onClick={handleFileOpen}>
                                                        عرض الملف
                                                    </Button>
                                                    <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={handleDeleteFile}>
                                                        حذف
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                لا يوجد ملف مرفوع حالياً. يمكنك رفع ملف PDF أو TXT لتدريب البوت.
                                            </Typography>
                                        )}

                                        <Button
                                            variant="outlined"
                                            component="label"
                                            startIcon={<UploadFileIcon />}
                                            sx={{ mt: 1 }}
                                        >
                                            {selectedFile ? selectedFile.name : "اختر ملف (PDF/TXT)"}
                                            <input
                                                type="file"
                                                hidden
                                                accept=".pdf,.txt"
                                                onChange={handleFileChange}
                                            />
                                        </Button>
                                        {selectedFile && (
                                            <Button color="warning" onClick={() => setSelectedFile(null)} sx={{ ml: 1, mt: 1 }}>
                                                إلغاء التحديد
                                            </Button>
                                        )}
                                    </Box>
                                </Paper>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={saving}
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                        sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                                    >
                                        {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                                    </Button>
                                </Box>
                            </Stack>
                        </Box>
                    </TabPanel>

                    {/* Tab 2: SMTP */}
                    <TabPanel value={value} index={1}>
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 3 }}>
                                بيانات خادم البريد (SMTP)
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        label="SMTP Host"
                                        name="email_host"
                                        value={formData.email_host}
                                        onChange={handleInputChange}
                                        placeholder="smtp.gmail.com"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="SMTP Port"
                                        name="email_port"
                                        value={formData.email_port}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="اسم المستخدم"
                                        name="email_host_user"
                                        value={formData.email_host_user}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="كلمة المرور"
                                        name="email_host_password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.email_host_password}
                                        onChange={handleInputChange}
                                        placeholder="اتركه فارغاً للاحتفاظ بالحالية"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="عنوان المرسل (From)"
                                        name="email_from_address"
                                        value={formData.email_from_address}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ mt: 1 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.email_use_tls}
                                                    onChange={handleInputChange}
                                                    name="email_use_tls"
                                                    color="primary"
                                                />
                                            }
                                            label="استخدام تشفير TLS"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, flexWrap: 'wrap' }}>
                                <TextField
                                    size="small"
                                    label="إرسال اختبار إلى"
                                    placeholder="example@email.com"
                                    value={formData.testEmail || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, testEmail: e.target.value }))}
                                    sx={{ minWidth: 250 }}
                                />
                                <Button
                                    variant="outlined"
                                    color="info"
                                    disabled={testingEmail || !formData.email_host}
                                    onClick={handleTestEmail}
                                    startIcon={testingEmail ? <CircularProgress size={20} /> : <EmailIcon />}
                                >
                                    {testingEmail ? 'جاري الإرسال...' : 'اختبار الإعدادات'}
                                </Button>
                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={saving}
                                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                >
                                    حفظ الإعدادات
                                </Button>
                            </Box>
                        </Box>
                    </TabPanel>
                </Paper>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarOpen(false)}
                    message={snackbarMessage}
                />
            </Container>
        </motion.div>
    );
}
